import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { api, formatApiErrorDetail } from "../lib/api";
import { Plus, Trash, PencilSimple, X, Buildings, ArrowRight } from "@phosphor-icons/react";

export default function AdminCompaniesPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");

  const fetchList = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/companies");
      setList(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, []);

  const openCreate = () => { setEditing(null); setName(""); setDescription(""); setError(""); setShowForm(true); };
  const openEdit = (c) => { setEditing(c); setName(c.name); setDescription(c.description || ""); setError(""); setShowForm(true); };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editing) {
        await api.put(`/companies/${editing.id}`, { name, description });
      } else {
        await api.post("/companies", { name, description });
      }
      setShowForm(false);
      fetchList();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const onDelete = async (c) => {
    if (!window.confirm(`Bedrijf ${c.name} en alle work coaches en coachees verwijderen?`)) return;
    await api.delete(`/companies/${c.id}`);
    fetchList();
  };

  return (
    <AppLayout title="Work coach bedrijven" breadcrumbs={[{ label: "Admin" }, { label: "Bedrijven" }]}>
      <div className="flex items-center justify-between mb-6">
        <div className="text-sm text-zinc-500">{list.length} bedrijf{list.length !== 1 ? "ven" : ""}</div>
        <button
          onClick={openCreate}
          data-testid="add-company-button"
          className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors"
        >
          <Plus size={16} /> Bedrijf toevoegen
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-500">Laden...</div>
      ) : list.length === 0 ? (
        <div className="neuro-card p-12 text-center" data-testid="companies-empty">
          <Buildings size={32} className="mx-auto text-zinc-300" />
          <h3 className="font-display text-lg font-bold mt-3 tracking-tight">Nog geen bedrijven</h3>
          <p className="text-sm text-zinc-500 mt-1">Voeg een work coach bedrijf toe om aan de slag te gaan.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="companies-grid">
          {list.map((c) => (
            <Link
              key={c.id}
              to={`/companies/${c.id}`}
              data-testid={`company-card-${c.id}`}
              className="neuro-card-bold p-5 hover:shadow-md transition-shadow flex flex-col text-left no-underline text-inherit"
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="inline-flex h-10 w-10 items-center justify-center bg-[#1E88E5] text-white rounded-sm">
                  <Buildings size={20} weight="bold" />
                </div>
                <div className="flex gap-1">
                  <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEdit(c); }} data-testid={`edit-company-${c.id}`} className="text-zinc-400 hover:text-zinc-900 p-1.5"><PencilSimple size={16} /></button>
                  <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(c); }} data-testid={`delete-company-${c.id}`} className="text-zinc-400 hover:text-[#E53935] p-1.5"><Trash size={16} /></button>
                </div>
              </div>
              <h3 className="font-display text-lg font-bold tracking-tight">{c.name}</h3>
              <p className="text-sm text-zinc-500 mt-1 line-clamp-2 min-h-[2.5em]">{c.description || "Geen beschrijving"}</p>
              <div data-testid={`open-company-${c.id}`} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-900 group">
                Work coaches bekijken <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      )}

      {showForm && (
        <Modal title={editing ? "Bedrijf bewerken" : "Nieuw bedrijf"} onClose={() => setShowForm(false)}>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="company-form">
            <Field label="Naam" value={name} onChange={setName} testid="company-name-input" />
            <Field label="Beschrijving" value={description} onChange={setDescription} testid="company-description-input" required={false} multiline />
            {error && <div className="text-sm text-[#E53935]" data-testid="company-form-error">{error}</div>}
            <button type="submit" data-testid="company-form-submit" className="w-full bg-zinc-900 text-white py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors">
              {editing ? "Opslaan" : "Toevoegen"}
            </button>
          </form>
        </Modal>
      )}
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Shared Modal & Field components (re-exported so other pages can use them)
// ---------------------------------------------------------------------------
export function Modal({ children, title, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 fade-up" data-testid="modal">
      <div className="bg-white border border-zinc-200 rounded-md w-full max-w-md p-6 shadow-xl">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-lg font-bold tracking-tight">{title}</h3>
          <button onClick={onClose} data-testid="modal-close" className="text-zinc-400 hover:text-zinc-900"><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, type = "text", value, onChange, testid, required = true, multiline = false }) {
  if (multiline) {
    return (
      <div>
        <label className="text-xs uppercase tracking-widest text-zinc-500 block mb-1.5">{label}</label>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          rows={3}
          data-testid={testid}
          className="w-full border border-zinc-300 focus:border-zinc-900 outline-none px-3 py-2.5 text-[15px] rounded-sm bg-white"
        />
      </div>
    );
  }
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-zinc-500 block mb-1.5">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="w-full border border-zinc-300 focus:border-zinc-900 outline-none px-3 py-2.5 text-[15px] rounded-sm bg-white"
      />
    </div>
  );
}
